import { useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  ArrowRight,
  Box,
  CalendarDays,
  ClipboardCheck,
  Copy,
  MoreHorizontal,
  NotebookPen,
  RefreshCw,
  Settings2,
  Table2,
  Wallet,
} from 'lucide-react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  RelativeTime,
  cn,
} from '@/components/ui';
import type {
  HomeApplicationIcon,
  HomeWidgetDefinition,
  HomeWidgetInstance,
  HomeWidgetState,
} from './homeWidgetData';
const appIcons = {
  box: Box,
  tasks: ClipboardCheck,
  calendar: CalendarDays,
  finance: Wallet,
  notes: NotebookPen,
};
export function HomeApplicationGlyph({ icon }: { icon: HomeApplicationIcon }) {
  const Icon = appIcons[icon];
  return <Icon className="size-4" aria-hidden />;
}
export interface HomeWidgetActions {
  onConfigure?: () => void;
  onViewTable?: () => void;
  onDuplicate?: () => void;
  onRetry?: () => void;
  onOpenApplication?: () => void;
  dragHandleProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  dragHandleRef?: (element: HTMLButtonElement | null) => void;
  removeAction?: ReactNode;
}
export function HomeWidgetFrame({
  definition,
  instance,
  state,
  children,
  now,
  dragHandleProps,
  dragHandleRef,
  removeAction,
  onConfigure,
  onDuplicate,
  onRetry,
  onOpenApplication,
  onViewTable,
}: HomeWidgetActions & {
  definition: HomeWidgetDefinition;
  instance: HomeWidgetInstance;
  state: HomeWidgetState;
  children: ReactNode;
  now?: number;
}) {
  const runAfterClose = useRef<(() => void) | null>(null),
    menuTriggerRef = useRef<HTMLButtonElement>(null);
  const hasMenu = !!(onConfigure || onViewTable || onDuplicate || onRetry);
  return (
    <section
      data-slot="home-widget"
      data-home-widget-type={definition.type}
      data-home-widget-state={state.state}
      data-home-instance={instance.instanceId}
      aria-label={definition.title}
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-card"
    >
      <header className="flex shrink-0 items-start gap-2 border-b border-line px-3 py-2">
        <span
          title={definition.application.title}
          aria-label={definition.application.title}
          className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md border border-line bg-muted text-fg-muted"
        >
          <HomeApplicationGlyph icon={definition.application.icon} />
        </span>
        <div className="min-w-0 flex-1 py-1">
          <h3 className="truncate text-xs font-semibold text-fg" title={definition.title}>
            {dragHandleProps ? (
              <button
                ref={dragHandleRef}
                type="button"
                {...dragHandleProps}
                data-home-drag-handle={dragHandleProps.disabled ? undefined : ''}
                className={cn(
                  'block w-full min-w-0 truncate rounded-xs text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  dragHandleProps.className,
                )}
              >
                {definition.title}
              </button>
            ) : (
              definition.title
            )}
          </h3>
          {state.state === 'ready' && state.attention && (
            <div className="mt-1">
              <Badge variant={state.attention.tone}>{state.attention.label}</Badge>
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              ref={menuTriggerRef}
              type="button"
              size="sm"
              variant="ghost"
              aria-label={'Меню виджета: ' + definition.title}
              disabled={!hasMenu}
            >
              <MoreHorizontal />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={(event) => {
              const run = runAfterClose.current;
              runAfterClose.current = null;
              if (run) {
                event.preventDefault();
                menuTriggerRef.current?.focus();
                run();
              }
            }}
          >
            {onConfigure && (
              <DropdownMenuItem
                leftIcon={<Settings2 />}
                onSelect={() => {
                  runAfterClose.current = onConfigure;
                }}
              >
                Настроить виджет
              </DropdownMenuItem>
            )}
            {onViewTable && (
              <DropdownMenuItem
                leftIcon={<Table2 />}
                onSelect={() => {
                  runAfterClose.current = onViewTable;
                }}
              >
                Данные таблицей
              </DropdownMenuItem>
            )}
            {onDuplicate && (
              <DropdownMenuItem leftIcon={<Copy />} onSelect={onDuplicate}>
                Добавить ещё один
              </DropdownMenuItem>
            )}
            {onRetry && (
              <DropdownMenuItem leftIcon={<RefreshCw />} onSelect={onRetry}>
                Обновить
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {removeAction}
      </header>
      <div data-home-widget-body="" className="min-h-0 min-w-0 flex-1 overflow-auto p-3">
        {children}
      </div>
      {((state.state === 'ready' && state.updatedAt) || definition.showOpenApplication) && (
        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-line px-3 py-2">
          {state.state === 'ready' && state.updatedAt && (
            <span className="text-2xs text-fg-subtle">
              Обновлено <RelativeTime at={state.updatedAt} now={now} />
            </span>
          )}
          {definition.showOpenApplication &&
            (onOpenApplication ? (
              <Button type="button" variant="link" size="sm" onClick={onOpenApplication}>
                Открыть в приложении
                <ArrowRight className="size-3.5" />
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-fg-subtle">
                Открыть в приложении
                <ArrowRight className="size-3.5" />
              </span>
            ))}
        </footer>
      )}
    </section>
  );
}
